<template>
  <base-map :vessels="vessels"
            :selected-vessel-imo="selectedVesselIMO"
            :highlighted-vessel-imo="highlightedVesselIMO"
            :map-bounds="mapBounds"
            @map-update="handleMapMove"
            @vessel-popup-ready="handleVesselPopupReady">

    <template #map-extras="{ context }">

      <!-- live vessels switch -->
      <l-control position="topright">
        <div @click="toggleLiveVessels(context)"
             :title="showVesselsInArea ? 'Hide vessels in the map' : 'Show vessels in the map'"
             class="live-vessels-switcher"
             :class="{'live-vessels-enabled': showVesselsInArea}">
          <CIcon :height="22" :content="$options.coreUIIcons.cilBoatAlt" />
        </div>
      </l-control>

      <!-- history voyages switch -->
      <l-control position="topright">
        <div v-if="historyVoyagesAvailable"
             @click="loadHistoryVoyages"
             :title="'Load more historical voyages'"
             class="voyages-download">
          <CIcon :height="22" :content="$options.coreUIIcons.cilCloudDownload" />
        </div>
      </l-control>

      <!-- berth circles -->
      <l-circle
        v-for="(berth, index) in berthsInternal"
        :lat-lng="berth.coordinates"
        :radius="berth.radius * 90000"
        :key="`berth-${index}`"
        :options="getBerthOptions(berth)">
        <l-popup :options="{ permanent: true, interactive: true }">
          <div class="berth-popup">
            <div class="berth-popup__row text-center">
              <strong>{{ berth.text }}</strong>
            </div>
            <div class="berth-popup__row text-center">
              <b-button :variant="berth.selected ? 'danger' : 'success'"
                        class="berth-toggle-button"
                        size="sm"
                        @click="toggleBerthSelected(berth)">
                <template v-if="berth.selected">Unselect the berth</template>
                <template v-else>Select the berth</template>
              </b-button>
            </div>
          </div>
        </l-popup>
      </l-circle>

      <!-- historical voyages track lines -->
      <template v-for="(voyage, voyageIndex) in voyages">
        <l-polyline
          v-for="(line, lineIndex) in voyage.lines"
          :key="`polyline-${voyageIndex}-${lineIndex}`"
          :color="getVoyageColor(voyage)"
          :lat-lngs="line">

          <voyage-map-popup :voyage="voyage" />
        </l-polyline>
      </template>

    </template>
  </base-map>
</template>


<script>
import pickBy from 'lodash-es/pickBy';
import cloneDeep from 'lodash-es/cloneDeep';
import getNested from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import moment from 'moment';

import L from 'leaflet';
import {
  LCircle,
  LPopup,
  LControl,
  LPolyline,
} from 'vue2-leaflet';

import { freeSet as coreUIIcons} from '@coreui/icons';
import { mapActions } from 'vuex';
import { formatTrackingPoints, leafletBoundsToApi } from '@/shared/coordinate-utils';
import { trackingPalette } from '@/shared/constants/colors';
import BaseMap from '@/base_components/BaseMap';
import VoyageMapPopup from '@/components/portscomparison/VoyageMapPopup';
import { getVesselData } from '@/shared/api_formatters/vessels';
import startCase from 'lodash-es/startCase';


export default {
  name: 'port-tradeflows-map',

  coreUIIcons,

  components: {
    BaseMap,
    VoyageMapPopup,
    LCircle,
    LPopup,
    LControl,
    LPolyline,
  },

  props: {
    historyVoyages: {
      type: Boolean,
      default: false,
    },

    berths: {
      type: Array,
      default: () => [],
    },

    voyageParameters: {
      type: Object,
      default: () => ({
        loadPortId: null,
        dischargePortId: null,
        segment: [],
        commodityId: [],
        fromDate: '2015-01-01',
        toDate: moment().format('YYYY-MM-DD'),
      }),
    },
  },

  data() {
    return {
      mapBounds: null,
      berthsInternal: [],
      vessels: [],
      voyages: [],
      voyagesPage: 1,
      voyagesPageMax: 30,  // expected max page overall
      selectedVesselIMO: null,
      highlightedVesselIMO: null,
      voyageColorFields: ['segment', 'commodityGroup'],
      voyageColorAlpha: 0.5,

      // other settings
      showVesselsInArea: false,
    };
  },

  methods: {
    ...mapActions('api/vessels', [
      'getVesselsInArea',
      'getHistoricalVoyages',
    ]),

    async handleMapReady(mapObject) {
      this.map = mapObject;
      this.berthsInternal = this.berths;
    },

    async handleMapMove(coordinates) {
      if(this.showVesselsInArea) {
        await this.loadVesselsInArea(coordinates);
      } else {
        this.vessels = [];
      }
    },

    async loadVesselsInArea(coordinates) {
      let vessels = await this.getVesselsInArea(coordinates);
      vessels = getNested(vessels, 'vessels', []) || [];
      this.vessels = vessels.map(item => ({
        ...item,
        popupData: this.getVesselPopupData(item)
      }));
    },

    getVesselPopupData(vessel) {
      const popupData = getVesselData(vessel);
      return Object.keys(popupData).map(fieldName => ({
        name: startCase(fieldName),
        value: popupData[fieldName],
      }));
    },

    toggleBerthSelected(berth) {
      this.$emit('berth-updated', cloneDeep({...berth, selected: !berth.selected}));
    },

    async toggleLiveVessels(context) {
      const map = getNested(context, 'map.instance', null);
      if(!map) {
        return;
      }

      this.showVesselsInArea = !this.showVesselsInArea;
      if(this.showVesselsInArea) {
        await this.handleMapMove(leafletBoundsToApi(map.getBounds()));
      } else {
        this.vessels = [];
      }
    },

    async loadHistoryVoyages() {
      if(this.historyVoyagesAvailable) {
        const apiData = {
          ...this.voyageParameters,
          loadBerthId: this.loadBerthId,
          dischargeBerthId: this.dischargeBerthId,
          page: this.voyagesPage,
        };
        // console.log('loadHistoryVoyages, apiData', JSON.stringify(apiData, null, 2));
        // console.log('loadHistoryVoyages, page', this.voyagesPage);

        let response = await this.getHistoricalVoyages(apiData);
        let voyages = getNested(response, 'data', []).map(voyage => {
          let points = voyage.vesselPositions.map(
            point => ({position: [point.lat, point.lon]})
          );
          return {
            ...pickBy(voyage, (value, name) => name !== 'vesselPositions'),
            lines: formatTrackingPoints(points || []),
          };
        })

        this.voyages = this.voyages.concat(voyages);
        this.voyagesPage += 1;
        this.voyagesPageMax = getNested(response, 'maxPage', 1);
      }
    },

    handleVesselPopupReady(evt) {
      evt._source.on('popupopen', evt => {
        this.selectedVesselIMO = getNested(evt, 'popup.options.itemId', null);
        this.highlightedVesselIMO = getNested(evt, 'popup.options.itemId', null);
      });

      evt._source.on('popupclose', () => {
        this.selectedVesselIMO = null;
        this.highlightedVesselIMO = null;
      });
    },

    getBerthsByDirection(direction) {
      return this.berthsInternal
        .filter(berth => berth.direction === direction && berth.selected)
        .map(berth => berth.value);
    },

    setMapBounds() {
      // more precise map centering and zooming after all enteties have been loaded
      if(isEmpty(this.berths)) {
        this.mapBounds = null;
        return;
      }

      const coordinates = this.berths.map(berth => berth.coordinates);
      const lats = coordinates.map(item => item[0]);
      const longs = coordinates.map(item => item[1]);

      // mapBounds goes to the <base-map> props and the watcher will update leaflet bounds
      this.mapBounds = [
        L.latLng(Math.min(...lats), Math.min(...longs)),
        L.latLng(Math.max(...lats), Math.max(...longs)),
      ];
    },

    getBerthOptions(berth) {
      let style = {
        weight: 2,
        color: '#808080',
        fillColor: '#808080',
        dashArray: '',
        opacity: 0.3,
        fillOpacity: 0.4,
      };

      if(berth.selected) {
        style.opacity = 0.2;
        style.color = '#22a30e';
        style.fillColor = '#22a30e';
        style.fillOpacity = 0.5;
      }
      return style;
    },

    getVoyageColor(voyage) {
      const colorKey = this.getVoyageColorKey(voyage);
      const fallbackColor = 'rgba(255, 255, 255, 0.5)';
      return getNested(this.voyagesPalette, `${colorKey}.rgbaColor`, fallbackColor);
    },

    getVoyageColorKey(voyage) {
      let fieldValuesKey = this.voyageColorFields.map(fieldName => getNested(voyage, fieldName));
      return fieldValuesKey.map(value => value.toLowerCase()).join('-');
    },

    getPaletteIdByKey(colorKey, paletteSize) {
      // sum all charcodes and divide by palette size
      // it should be enough for hashing since colorKey has human readable format
      // (no pure permutations expected here)

      const sum  = [...colorKey].map(
        char => char.charCodeAt(0)
      ).reduce(
        (prev, current) => prev + current, 0
      );

      return sum % paletteSize;
    },

    getRGBAColorByHex(hexColor, alpha) {
      const red = parseInt(hexColor.slice(1, 3), 16);
      const green = parseInt(hexColor.slice(3, 5), 16);
      const blue = parseInt(hexColor.slice(5, 7), 16);

      return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    }
  },

  computed: {
    loadBerthId() {
      return this.getBerthsByDirection('load');
    },

    dischargeBerthId() {
      return this.getBerthsByDirection('discharge');
    },

    historyVoyagesAvailable() {
      return (
        this.historyVoyages &&
        (
          !isEmpty(this.loadBerthId) || !isEmpty(this.voyageParameters.loadPortId)
        ) && (
          !isEmpty(this.dischargeBerthId) || !isEmpty(this.voyageParameters.dischargePortId)
        ) &&
        this.voyagesPage <= this.voyagesPageMax
      )
    },

    voyagesPalette() {
      // generates palette according to voyages data
      // {<field-combination-string>: color}
      // where <field-combination-string> is "panamax-coal" for example (lower case)

      let palette = {};
      let voyageColorKey;

      this.voyages.forEach(voyage => {
        voyageColorKey = this.getVoyageColorKey(voyage);

        if(!Object.prototype.hasOwnProperty.call(palette, voyageColorKey)) {
          const paletteIndex = this.getPaletteIdByKey(voyageColorKey, trackingPalette.length);

          palette[voyageColorKey] = {
            paletteIndex,
            color: trackingPalette[paletteIndex],
            rgbaColor: this.getRGBAColorByHex(trackingPalette[paletteIndex], this.voyageColorAlpha),
          };
        }
      });

      // console.log('new palette', JSON.stringify(palette, null, 2));
      return palette;
    },
  },

  watch: {
    berths(berths) {
      // console.log('tradeflows map watch berths', berths);
      this.setMapBounds();
      this.berthsInternal = [];
      this.voyages = [];
      this.voyagesPage = 1;
      this.voyagesPageMax = 30;

      this.$nextTick(async () => {
        this.berthsInternal = berths;
        await this.loadHistoryVoyages();
      });
    },
  },
};
</script>


<style scoped lang="scss">
.map-mode {
  position: absolute;
  bottom: 15px;
  right: 10px;
  z-index: 500;
}

.map-container {
  z-index: 10;
  padding: 0;
  position: relative;
  min-height: 370px;

  .berth-popup {
    &__row {
      margin-bottom: 0.7rem;
    }

    .berth-toggle-button {
      white-space: nowrap;
    }

    .berth-toggle-button:active {
      box-shadow: none !important;
    }

    .berth-toggle-button:focus {
      box-shadow: none !important;
    }
  }
  // live-vessels toggle map button
  .live-vessels-switcher {
    cursor: pointer;
    color: #ffffff;
    filter: drop-shadow( 1px 1px 1px rgba(0, 0, 0, 0.3));

    &:hover {
      color: #20a8cd;
    }

    &.live-vessels-enabled { color: #00749a; }
    &.live-vessels-enabled:hover { color: #29c1ea; }
  }

  // voyages download map button
  .voyages-download {
    cursor: pointer;
    color: #ffffff;
    filter: drop-shadow( 1px 1px 1px rgba(0, 0, 0, 0.3));

    &:hover {
      color: #20a8cd;
    }
  }
}
</style>
